import { useQuery, useQueryClient } from "@tanstack/react-query";
import { atom, createStore } from "jotai";
import { logger } from "@artis/logger";
import toast from "react-hot-toast";
import { useCallback, useEffect } from "react";
import z from "zod";

import type { useHistorical } from "../live-charts/chart/hooks/useChartData/useHistorical";
import { getLiveStreamConnects } from "../live-charts/chart/hooks/useChartData/useLive";
import { useOptionalActivePageId, usePageProducts } from "../../data";
import type { TColumnHeaderProps } from "../market-grid/components";
import { useProductConfigsCache } from "../../data/cached/preload";
import { useSuspenseFn } from "../../utils/useSuspenseFn";
import { getSyncBrotli } from "../../utils/encoding";
import { downloadStringAsFile } from "./helpers";
import { useCalcs } from "../calcs/react";
import type { colParams } from "../utils";
import type { TestChartHistoricalEntry, TestWindow } from "./types";
import { isDebugMode } from "../../globals";
import { sharedWorkerDate } from "../calcs/main";
import {
  getCapturedChartSettings,
  getComputedChartCandles,
  getComputedChartIds,
} from "../live-charts/chart/components/ExpressionModifiers";
import { useCurrentUser } from "../../data/user";
import { useUserId } from "../../context/auth/hooks";

const buildInfoSchema = z.object({
  sha: z.string(),
  date: z.string(),
});

// Fetch build info from /build-info.json
export function useBuildInfo() {
  return useQuery({
    queryKey: ["buildInfo"],
    queryFn: async () => {
      try {
        const res = await fetch("/build-info.json", { cache: "no-store" });
        if (!res.ok) throw new Error("Failed to fetch build info");
        const info = await res.json();
        const parsed = buildInfoSchema.safeParse(info);
        return {
          sha: parsed.success ? parsed.data.sha : null,
          date: parsed.success ? parsed.data.date : null,
        };
      } catch (error) {
        logger.error("Error fetching build info", { error });
        return { sha: "unknown", date: "unknown" };
      }
    },
  });
}

type TPerformanceMeasure = () => Promise<unknown>;

export function useDownloadDebugData() {
  const userId = useUserId();
  const cache = useProductConfigsCache();
  const user = useCurrentUser().data;
  const { debugGetCachedProductValues } = useCalcs();
  const pageProducts = usePageProducts(useOptionalActivePageId()).data;
  const buildInfo = useBuildInfo().data;
  const queryClient = useQueryClient();
  const brotli = useSuspenseFn("brotli", getSyncBrotli);

  const download = useCallback(
    async (data: Record<string, unknown>, compress: boolean) => {
      if (debugCurrentGridData.logDownloaded) {
        toast.error("Log data already downloaded recently");
      }

      debugCurrentGridData.logDownloaded = true;

      toast.loading("Downloading log data...", {
        id: "debug-load",
        duration: compress ? 30_000 : 10_000,
      });

      data.userId = userId;
      data.email = user?.email || "unknown";
      data.username = user?.username || "unknown";
      data.subscriptionTier = user?.subscription_tier || "unknown";
      data.buildInfo = buildInfo || { sha: "unknown", date: "unknown" };

      if (buildInfo?.sha) {
        data.commit = `https://github.com/artisworks/artis-webapp/commit/${buildInfo.sha}`;
      }

      data.userAgent = navigator.userAgent;
      data.timestamp = new Date().toISOString();
      data.url = window.location.href;

      if ("measureUserAgentSpecificMemory" in performance) {
        try {
          const memoryPromise = (performance.measureUserAgentSpecificMemory as TPerformanceMeasure)();
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Memory measurement timed out")), 20_000),
          );
          const memory = await Promise.race([memoryPromise, timeoutPromise]);
          data.memory = memory;
        } catch (e) {
          data.memory = `Error measuring memory: ${JSON.stringify(e)}`;
        }
      }

      data.useQueryCache = queryClient.getQueryCache().getAll();
      data.useMutationCache = queryClient.getMutationCache().getAll();
      data.localStorage = { ...localStorage };
      data.sessionStorage = { ...sessionStorage };

      const cachedProductValues = [];
      for (const p of pageProducts || []) {
        if (!p.product_id) continue;
        const val = await debugGetCachedProductValues(p.product_id);
        cachedProductValues.push(val);
      }

      data.calcWorkerCacheData = cachedProductValues;

      data.prodAndConfigCache = cache.value();

      toast.success("Downloaded log data", {
        duration: 10_000,
        icon: "⚠️",
      });

      debugCurrentGridData.logDownloaded = false;

      if (!compress) {
        downloadStringAsFile(JSON.stringify(data, null, 2), `artis-debug-log-${new Date().toISOString()}.json`);
        return;
      }

      const compressed = brotli.compress(JSON.stringify(data));
      downloadStringAsFile(compressed, `artis-debug-log-${new Date().toISOString()}.txt`);
    },
    [userId, user, debugGetCachedProductValues, pageProducts, buildInfo, queryClient, brotli, cache],
  );

  return { download };
}

export type TCurrentData = {
  // columnId is key, rowId is the nested key
  [key: string]: {
    [key: string]: number | string | undefined | TColumnHeaderProps;
    colParams: ReturnType<typeof colParams>;
  };
};

const store = createStore();
export const debugCurrentGridDataAtom = atom<TCurrentData[]>([]);
export const debugCurrentGridData = {
  logDownloaded: false,
  get: () => store.get(debugCurrentGridDataAtom),
  push: (data: TCurrentData) => {
    store.set(debugCurrentGridDataAtom, (old) => [...old, data]);
  },
  shift: () => {
    store.set(debugCurrentGridDataAtom, (old) => {
      const [, ...rest] = old;
      return rest;
    });
  },
  length: () => {
    return store.get(debugCurrentGridDataAtom).length;
  },
  clear: () => {
    store.set(debugCurrentGridDataAtom, []);
  },
};

// In debug/dev mode, we expose some functions to the window object so that they can be called from our E2E tests. The query client and many of its functions cannot be passed directly to the window object because of serialization issues, but we can create wrapper functions that access the query client and return serializable data.
export const useExposeTestFunctions = () => {
  const w = window as unknown as Window & TestWindow;

  const queryClient = useQueryClient();
  const userId = useUserId();

  useEffect(() => {
    if (!isDebugMode) return;
    w._getSharedWorkerDate = () => sharedWorkerDate;
    w._getCurrentUser = () => queryClient.getQueryData(["currentUser", userId]);
    w._getChartData = () =>
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ["chartHistorical"], exact: false })
        ?.map((query) => query.state.data) as ReturnType<typeof useHistorical>["historical"]["data"][];
    w._refetchLiveChartPages = async () => {
      await queryClient.invalidateQueries({ queryKey: ["live_chart_page", userId] });
      await queryClient.refetchQueries({ queryKey: ["live_chart_page", userId] });
    };
    w._getLiveStreamConnects = () => getLiveStreamConnects();
    w._getComputedChartCandles = (chartId: string) => getComputedChartCandles(chartId);
    w._getComputedChartIds = () => getComputedChartIds();
    w._getChartSettings = (chartId: string) => getCapturedChartSettings(chartId);
    w._getChartHistoricalEntries = () =>
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ["chartHistorical"], exact: false })
        .map((query) => {
          // Shape: ["chartHistorical", productId, sampleTime, periodFrom, expression]
          const key = query.queryKey as [string, string | undefined, string | undefined, string | undefined, unknown];
          return {
            productId: key[1] ?? "",
            periodFrom: key[3] ?? "",
            sampleTime: key[2],
            data: query.state.data,
          } as TestChartHistoricalEntry;
        });
  }, [queryClient, userId, w]);
};
